import { AlertCircle, CheckCircle2, Circle, Clock, XCircle } from 'lucide-react';
import { cn } from '@tsgecart/ui';

export type AdminStatusTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

const TONE_STYLES: Record<AdminStatusTone, string> = {
  neutral: 'bg-gray-100 text-gray-700',
  success: 'bg-green-100 text-green-800',
  warning: 'bg-amber-100 text-amber-900',
  danger: 'bg-red-100 text-red-800',
  info: 'bg-blue-100 text-blue-800',
};

const TONE_ICONS: Record<AdminStatusTone, typeof Circle> = {
  neutral: Circle,
  success: CheckCircle2,
  warning: AlertCircle,
  danger: XCircle,
  info: Clock,
};

export interface AdminStatusBadgeProps {
  label: string;
  tone?: AdminStatusTone;
  className?: string;
}

/** Every tone pairs a distinct icon with text -- never conveys status by color alone (Part 16). */
export function AdminStatusBadge({ label, tone = 'neutral', className }: AdminStatusBadgeProps) {
  const Icon = TONE_ICONS[tone];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium capitalize',
        TONE_STYLES[tone],
        className,
      )}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      {label}
    </span>
  );
}

const ORDER_STATUS_TONE: Record<string, AdminStatusTone> = {
  pending_payment: 'warning',
  confirmed: 'info',
  processing: 'info',
  out_for_delivery: 'info',
  delivered: 'success',
  cancelled: 'danger',
  refunded: 'neutral',
};

const PAYMENT_STATUS_TONE: Record<string, AdminStatusTone> = {
  pending: 'warning',
  authorized: 'info',
  captured: 'success',
  failed: 'danger',
  refunded: 'neutral',
  partially_refunded: 'neutral',
};

const NOTIFICATION_STATUS_TONE: Record<string, AdminStatusTone> = {
  pending: 'warning',
  sent: 'info',
  delivered: 'success',
  failed: 'danger',
  skipped: 'neutral',
};

/** `refunds.status` (Admin Dashboard Milestone 3) -- plain `pending | processed | failed`, no dedicated Postgres enum (see database-schema.md), but still gets the same tone-mapped badge treatment as every other status field. */
const REFUND_STATUS_TONE: Record<string, AdminStatusTone> = {
  pending: 'warning',
  processed: 'success',
  failed: 'danger',
};

function humanize(status: string): string {
  return status.replace(/_/g, ' ');
}

export function AdminOrderStatusBadge({ status }: { status: string }) {
  return <AdminStatusBadge label={humanize(status)} tone={ORDER_STATUS_TONE[status] ?? 'neutral'} />;
}

export function AdminPaymentStatusBadge({ status }: { status: string }) {
  return <AdminStatusBadge label={humanize(status)} tone={PAYMENT_STATUS_TONE[status] ?? 'neutral'} />;
}

export function AdminNotificationStatusBadge({ status }: { status: string }) {
  return (
    <AdminStatusBadge label={humanize(status)} tone={NOTIFICATION_STATUS_TONE[status] ?? 'neutral'} />
  );
}

export function AdminRefundStatusBadge({ status }: { status: string }) {
  return <AdminStatusBadge label={humanize(status)} tone={REFUND_STATUS_TONE[status] ?? 'neutral'} />;
}
